import React, { PropsWithChildren, FC, useCallback, useState, useEffect } from "react";

import {
    OnboardingAside,
    OnboardingContent,
    Input,
    PageAlert,
    Suggestion,
    PrimaryAlternateControl,
    PlacesAutocomplete,
    Place,
    AutoCompleteSuggestionsInput,
} from "@xapp/components";

import { Template, TEMPLATES } from "constants/templates";
import { getTemplateFromPlace } from "components/Onboarding/utils/getTemplateFromPlace";

import { OnboardingProps } from "../../models";

import classes from "./BusinessInfoStep.module.scss";

interface TemplatedSuggestion extends Suggestion, Template {}

const DEFAULT_TEMPLATE: Template = TEMPLATES.find((template) => template.name === "Other");

//const DEFAULT_SUGGESTION: TemplatedSuggestion = {
//    ...DEFAULT_TEMPLATE,
//    label: DEFAULT_TEMPLATE.name,
// };

export interface BusinessInfoStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Site data used to pre-populate fields
     */
    initialProps?: OnboardingProps;
    /**
     * Is the app being created
     */
    creating?: boolean;
    /**
     * Status message displayed to the user to keep them updated on the creation process
     */
    creatingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string; message: string };
    /**
     * Show beta features
     */
    beta?: boolean;
    /**
     * Ask for template selection
     */
    askForTemplate?: boolean;
    /**
     * The user has requested to create an app based on the provided properties.
     */
    onNext(props: OnboardingProps): void;
    /**
     * The user wants to go back
     */
    onBack?(): void;
}

/**
 * V3 Version, incorporates Google Autocomplete features
 *
 * @param props
 * @returns
 */
export const BusinessInfoStep: FC<PropsWithChildren<BusinessInfoStepProps>> = (props) => {
    const { style, className, aside, error, initialProps, askForTemplate, onBack, onNext } = props;

    // We get initial props from website loads or
    const [createProps, setCreateProps] = useState<OnboardingProps>(initialProps);
    // Used to monitor if we are waiting for a place to be loaded after an address was selected
    const [waitingForPlace, setWaitingForPlace] = useState<boolean>(false);

    const [template, setTemplate] = useState<Template | undefined>(initialProps?.template);

    useEffect(() => {
        if (initialProps) {
            setCreateProps({
                ...initialProps,
            });
        }
    }, [initialProps, setCreateProps]);

    const handleBusinessSearch = useCallback(
        (value: string) => {
            setCreateProps({
                ...createProps,
                name: value,
                // clear the place
                place: undefined,
            });
        },
        [createProps]
    );

    const handlePlaceId = useCallback(
        (placeId: string) => {
            setWaitingForPlace(true);
            setCreateProps({
                ...createProps,
                place: {
                    ...createProps.place,
                    placeId: placeId,
                },
            });
        },
        [createProps]
    );

    const handlePlace = useCallback(
        (place: Place) => {
            console.log("placed received");
            console.log(place);

            const templateFromPlace = getTemplateFromPlace(place);

            setCreateProps({
                ...createProps,
                name: place.name,
                website: place.website,
                place: place,
                searchAddress: place.formattedAddress,
                template: templateFromPlace,
            });

            if (templateFromPlace) {
                setTemplate(templateFromPlace);
            }
        },
        [createProps]
    );

    const handleAddressSearch = useCallback(
        (value: string) => {
            setCreateProps({
                ...createProps,
                searchAddress: value,
                beta: {
                    ...createProps?.beta,
                    onboarding: {
                        ...createProps?.beta?.onboarding,
                        searchAddress: value,
                    },
                },
            });
        },
        [createProps]
    );

    const handleAddress = useCallback(
        (place: Place) => {
            const searchAddress: string = place.formattedAddress;

            // get city state from the address components
            // const city = place.addressComponents?.find((component) => component.types.includes("locality"));
            // const state = place.addressComponents?.find((component) =>
            //     component.types.includes("administrative_area_level_1")
            // );
            // we prefer to display city and state instead of the full address, otherwise we just display what they select?
            //   if (city && state) {
            //       searchAddress = `${city?.longName}, ${state?.shortName}`;
            //   }

            setCreateProps({
                ...createProps,
                searchAddress,
                beta: {
                    ...createProps?.beta,
                    onboarding: {
                        ...createProps?.beta?.onboarding,
                        searchAddress,
                    },
                },
            });
        },
        [createProps]
    );

    const handleTemplateChange = useCallback(
        (template: TemplatedSuggestion) => {
            // if it is the "input-suggestion"
            if (template.id === "input-suggestion") {
                template.name = template.label;
                template.id = "contact-capture";
                template.url = DEFAULT_TEMPLATE?.url;
            }

            if (template.id === "echo") {
                template.name = template.label;
                template.id = "contact-capture";
                template.url = DEFAULT_TEMPLATE?.url;
            }

            setCreateProps({
                ...createProps,
                template,
                beta: {
                    ...createProps.beta,
                    onboarding: {
                        ...createProps?.beta?.onboarding,
                        template,
                    },
                },
            });

            setTemplate(template);
        },
        [setCreateProps, createProps]
    );

    const handleOtherIndustryChange = useCallback(
        (industry: string) => {
            setCreateProps({
                ...createProps,
                beta: {
                    ...createProps?.beta,
                    onboarding: {
                        ...createProps?.beta?.onboarding,
                        otherIndustry: industry,
                    },
                },
            });
        },
        [createProps]
    );

    const handleNextClick = useCallback(() => {
        onNext(createProps);
    }, [createProps, onNext]);

    const businessValidation = (): { disabled: boolean; guidance: string } => {
        if (!createProps?.name) {
            return { disabled: true, guidance: "Business name is required." };
        }

        if (createProps?.name?.length < 2) {
            return { disabled: true, guidance: "Business name is too short." };
        }

        if (createProps?.name?.length > 150) {
            return { disabled: true, guidance: "Business name is too long." };
        }

        return { disabled: false, guidance: "" };
    };

    const bizValid = businessValidation();

    // Sorted our templates alphabetically by name
    const sortedTemplates: Suggestion[] = TEMPLATES.sort((a, b) => a.name.localeCompare(b.name)).map((template) => ({
        label: template.name,
        id: template.id,
    }));

    const disabled = !createProps.name || !createProps.searchAddress || bizValid.disabled;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent style={style} className={className}>
                {error ? (
                    <PageAlert background="orange">
                        <p>
                            We had a problem, please try again. If it continues, please email support at support@xapp.ai
                        </p>
                        <p>Error: {error.message}</p>
                        <p>Details:</p>
                        <pre style={{ overflow: "scroll" }}>{error.error}</pre>
                        {/**We need a call to action for them to get help */}
                    </PageAlert>
                ) : (
                    <></>
                )}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            rowGap: "25px",
                            width: "100%",
                            flexDirection: "column",
                            alignItems: "left",
                            justifyContent: "left",
                        }}
                    >
                        <div>
                            <div className={classes.showMobile} style={{ marginBottom: "10px" }}>
                                <p>We will use this information to look up your Google Business Profile.</p>
                            </div>

                            <b>What is the name of your business?</b>

                            <PlacesAutocomplete
                                variation="outlined"
                                type={"text"}
                                label={"Business Name"}
                                required
                                value={createProps?.name || ""}
                                config={{
                                    disableAutocomplete: true,
                                    onlyDisplayBusinessName: true,
                                    onlyDisplayOnExactMatch: true,
                                    parrotInput: true,
                                    displaySuggestionsAfterCharacters: 5,
                                    autocompleteHints: {
                                        multipleSimilarResults:
                                            "If you have multiple locations, select your main location",
                                        hasResults: "One of these might be your business",
                                    },
                                }}
                                onSelect={handlePlaceId}
                                onPlace={handlePlace}
                                onChange={handleBusinessSearch}
                            />
                        </div>
                        <div>
                            <b>Where are you located?</b>
                            <PlacesAutocomplete
                                variation="outlined"
                                type={"text"}
                                disabled={!createProps.name || waitingForPlace}
                                loading={waitingForPlace}
                                label={"City, State"}
                                required
                                value={createProps?.searchAddress || ""}
                                onSelect={handlePlaceId}
                                onPlace={handleAddress}
                                onChange={handleAddressSearch}
                                config={{ onlyDisplayAddress: true }}
                                addressSearch={true}
                                placeholder={"Address"}
                            />
                        </div>
                        {askForTemplate && (
                            <>
                                <div>
                                    <b>What type of business do you have?</b>
                                    <p>
                                        We will use this to automatically configure some defaults for you. Pick from the
                                        list or provide in your own words.
                                    </p>
                                </div>
                                <div
                                    style={{
                                        display: "flex",
                                        rowGap: "5px",
                                        flexDirection: "column",
                                        alignItems: "left",
                                        justifyContent: "left",
                                    }}
                                >
                                    <b>What type of business do you have?</b>
                                    <p>
                                        We will use this to automatically configure some defaults for you. Pick from the
                                        list or provide in your own words.
                                    </p>
                                    <AutoCompleteSuggestionsInput
                                        variation="outlined"
                                        value={template?.name}
                                        echo={true}
                                        //     noResultsFallback={DEFAULT_SUGGESTION}
                                        label={"Enter your industry..."}
                                        // inputLabel={"Type in your own or select below"}
                                        suggestions={sortedTemplates}
                                        onChange={handleTemplateChange}
                                    />
                                    {createProps?.template?.name === "Other" && (
                                        <div style={{ marginTop: "10px" }}>
                                            <b>How would you describe your business?</b>
                                            <Input
                                                label="Industry"
                                                type={"text"}
                                                autoComplete="off"
                                                helperText={
                                                    "Sharing this will allow us to prioritize industries in the future"
                                                }
                                                value={createProps?.beta?.onboarding?.otherIndustry || ""}
                                                onChange={handleOtherIndustryChange}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <PrimaryAlternateControl
                        labels={{
                            primary: "Next",
                            alternate: "Back",
                        }}
                        disabled={{ primary: disabled, alternate: false }}
                        onPrimary={handleNextClick}
                        onAlternate={onBack}
                    />
                </div>
            </OnboardingContent>
        </>
    );
};
